import "./style.scss";

import { CloseOutlined, EditOutlined, SaveOutlined } from "@ant-design/icons";
import {
  Button,
  Drawer as AntdDrawer,
  DrawerProps as AntdDrawerProps,
  Form,
} from "antd";
import classNames from "clsx";
import * as React from "react";

import { DrawerSizeEnum, PlacementEnum } from "@/types";

interface DrawerProps extends Omit<AntdDrawerProps, "size"> {
  placement?: PlacementEnum;
  size?: DrawerSizeEnum;
  width?: string | number;
  title: string;
  subTitle: string;
  saveBtnText?: string;
  saveBtnIcon?: React.ReactNode;
  showBackBtn?: boolean;
  backBtnText?: string;
  onBack?: () => void;
  fullWidth?: boolean;
  extras?: React.ReactNode;
  hideHeader?: boolean;
  loading?: boolean;
  isViewMode?: boolean;
  disableButton?: boolean;
  enterUpdateMode?: () => void;
  children: React.ReactNode;
  onCancel?: () => void;
}

export const Drawer = ({
  placement = PlacementEnum.bottom,
  size = DrawerSizeEnum.small,
  width,
  title,
  subTitle,
  saveBtnText = "Save",
  saveBtnIcon = <SaveOutlined />,
  showBackBtn = false,
  backBtnText = "Back",
  onBack,
  fullWidth = false,
  extras,
  hideHeader = false,
  loading = false,
  isViewMode = false,
  disableButton = false,
  enterUpdateMode,
  children,
  ...rest
}: DrawerProps) => {
  const form = Form.useFormInstance();
  const drawerHeight =
    size === DrawerSizeEnum.medium
      ? "60vh"
      : size === DrawerSizeEnum.large
      ? "calc(100vh - 80px)"
      : undefined; // sm is handled by default

  return (
    <AntdDrawer
      placement={placement}
      closable={false}
      height={drawerHeight}
      width={width}
      className="custom_drawer"
      footer={
        Boolean(form) && (
          <div
            className={classNames(
              "flex justify-end gap-x-3",
              !fullWidth && "mx-auto max-w-screen-xl"
            )}
          >
            <Button
              onClick={rest?.onClose}
              disabled={disableButton}
              size="large"
              className=" border-none bg-neutral-200 px-10 py-1.5"
            >
              Cancel
            </Button>
            {showBackBtn && onBack && (
              <Button
                onClick={onBack}
                disabled={disableButton}
                size="large"
                className="border-none bg-neutral-300 px-10 py-1.5"
              >
                {backBtnText}
              </Button>
            )}
            {isViewMode ? (
              <Button
                type="primary"
                htmlType="button"
                icon={<EditOutlined />}
                disabled={disableButton}
                size="large"
                onClick={enterUpdateMode}
                className="flex items-center px-10 py-1.5 shadow-none"
              >
                Edit
              </Button>
            ) : (
              <>
                <Button
                  type="primary"
                  htmlType="button"
                  icon={saveBtnIcon}
                  disabled={disableButton}
                  loading={loading}
                  size="large"
                  onClick={form?.submit}
                  className="flex items-center px-10 py-1.5 shadow-none"
                >
                  {saveBtnText}
                </Button>
              </>
            )}
          </div>
        )
      }
      {...rest}
    >
      {/* Drawer custom header start */}
      {!hideHeader && (
        <div className="sticky top-0 z-10 bg-neutral-200">
          <div
            className={classNames(
              "flex gap-x-2 p-4",
              !fullWidth && "mx-auto max-w-screen-xl"
            )}
          >
            <div className="flex-1">
              <h2 className="text-xl font-semibold leading-tight text-primary-0">
                {title}
              </h2>
              <p className="mt-1 text-secondary-0">{subTitle}</p>
            </div>
            <div className="flex items-center gap-x-2">
              {extras && <div>{extras}</div>}
              <button
                onClick={rest?.onClose}
                className="h-8 w-8 cursor-pointer rounded-full border-none bg-neutral-300"
              >
                <CloseOutlined />
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Drawer custom header end */}

      <div
        className={classNames("px-4", !fullWidth && "mx-auto max-w-screen-xl")}
      >
        {children}
      </div>
    </AntdDrawer>
  );
};
